import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errorMessage, Payment } from "../../lib/api";
import { dateTime, money } from "../../lib/format";
import { StatusBadge } from "../StatusBadge";

const PENDING = ["pending_customer_approval", "pending_submission", "submitted"];

export function PaymentsList({ mock }: { mock: boolean }) {
  const qc = useQueryClient();
  const { data: payments } = useQuery<Payment[]>({ queryKey: ["payments"], queryFn: () => api.get("/payments").then((r) => r.data) });

  const simulate = async (p: Payment, action: string) => {
    try {
      await api.post(`/dev/gocardless/payments/${p.id}/${action}`);
      toast.success(`GoCardless webhook "payments.${action}" processed`);
      ["wallet", "payments", "ledger", "me"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div data-testid="payments-list">
      <div className="eyebrow">Bank payments</div>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl">Payments</h2>
      <div className="mt-6 border-t border-border">
        {(payments || []).length === 0 && <p className="py-6 text-sm text-neutral-500" data-testid="payments-empty">No collections yet.</p>}
        {(payments || []).map((p) => {
          const pending = PENDING.includes(p.status);
          return (
            <div key={p.id} className="border-b border-border py-4" data-testid={`payment-row-${p.id}`}>
              <div className="flex items-center justify-between gap-4 text-sm">
                <div>
                  <div>{p.description}</div>
                  <div className="text-xs text-neutral-500">{dateTime(p.created_at)} · {p.kind === "instant_bank" ? "Instant bank" : "Direct Debit"}{p.gc_payment_id ? ` · ${p.gc_payment_id}` : ""}{p.charge_date && pending ? ` · charges ${p.charge_date}` : ""}</div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={p.status} testId={`payment-status-${p.id}`} />
                  <span className="tabular-nums">{money(p.amount_pence)}</span>
                </div>
              </div>
              {mock && (pending || p.status === "confirmed") && (
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="text-neutral-400">Simulate bank:</span>
                  {pending && <button onClick={() => simulate(p, "confirmed")} className="underline underline-offset-4 hover:text-[var(--moss)]" data-testid={`simulate-confirmed-${p.id}`}>confirm</button>}
                  {pending && <button onClick={() => simulate(p, "failed")} className="underline underline-offset-4 hover:text-[var(--wine)]" data-testid={`simulate-failed-${p.id}`}>fail</button>}
                  {p.status === "confirmed" && <button onClick={() => simulate(p, "charged_back")} className="underline underline-offset-4 hover:text-[var(--wine)]" data-testid={`simulate-chargeback-${p.id}`}>charge back</button>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
